'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface CallHistoryProps {
  contactId: string;
  refreshTrigger: number;
}

const CallHistory = ({ contactId, refreshTrigger }: CallHistoryProps) => {
  const [loading, setLoading] = useState(true);
  const [calls, setCalls] = useState<any[]>([]);
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    fetchCallHistory();
  }, [contactId, refreshTrigger, limit]);

  const fetchCallHistory = async () => {
    setLoading(true);
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('check_in_calls')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      setCalls(data || []);
    } catch (error) {
      console.error('Error fetching call history:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (callId: string) => {
    setExpandedCallId(expandedCallId === callId ? null : callId);
  };

  const getWellnessColor = (score: number) => {
    if (score >= 80) return 'bg-success';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-error';
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getSummary = (call: any) => {
    const responseData = call.response_data || {};
    const mood = call.mood || 'unknown';
    const pain = responseData.pain_level || 'N/A';
    return `Mood: ${mood}, Pain: ${pain}/10, Medications: ${call.medications_taken ? 'Taken' : 'Missed'}`;
  };

  if (loading) {
    return (
      <div className="glass p-6 animate-pulse">
        <div className="h-6 bg-sage-200/30 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          <div className="h-16 bg-sage-200/30 rounded"></div>
          <div className="h-16 bg-sage-200/30 rounded"></div>
          <div className="h-16 bg-sage-200/30 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="glass">
      <div className="p-6 border-b border-sage-200/30">
        <h2 className="text-2xl font-heading font-semibold text-foreground">
          Recent Check-Ins
        </h2>
      </div>

      <div className="divide-y divide-sage-200/30">
        {calls.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="PhoneIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No check-in calls yet</p>
          </div>
        ) : (
          calls.map((call) => (
            <div key={call.id} className="p-5 hover:bg-muted/30 transition-smooth">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-3 h-3 rounded-full ${getWellnessColor(call.wellness_score || 0)}`}></div>
                    <div>
                      <h3 className="font-heading font-semibold text-foreground">
                        {new Date(call.created_at).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {new Date(call.created_at).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                        {' · '}
                        {formatDuration(call.call_duration || 0)}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{getSummary(call)}</p>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-2xl font-bold text-foreground">
                      {call.wellness_score || 0}
                    </div>
                    <div className="text-xs text-muted-foreground">Score</div>
                  </div>
                  <button
                    onClick={() => toggleExpand(call.id)}
                    className="px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-smooth font-medium text-sm"
                  >
                    {expandedCallId === call.id ? 'Hide' : 'View'} Details
                  </button>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedCallId === call.id && (
                <div className="mt-4 pt-4 border-t border-sage-200/30">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <h4 className="font-semibold text-foreground mb-2">Extracted Data Points</h4>
                      <div className="space-y-1 text-sm">
                        <p className="text-foreground">
                          <span className="text-muted-foreground">Medications:</span>{' '}
                          {call.medications_taken ? '✅ Taken' : '❌ Missed'}
                        </p>
                        <p className="text-foreground">
                          <span className="text-muted-foreground">Mood:</span> {call.mood || 'N/A'}
                        </p>
                        <p className="text-foreground">
                          <span className="text-muted-foreground">Pain Level:</span>{' '}
                          {call.response_data?.pain_level || 'N/A'}/10
                        </p>
                        <p className="text-foreground">
                          <span className="text-muted-foreground">Wellness Score:</span>{' '}
                          {call.wellness_score || 0}/100
                        </p>
                        <p className="text-foreground">
                          <span className="text-muted-foreground">Cognitive Score:</span>{' '}
                          {call.cognitive_score || 0}/100
                        </p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold text-foreground mb-2">Red Flags</h4>
                      {call.red_flags && call.red_flags.length > 0 ? (
                        <div className="space-y-1">
                          {call.red_flags.map((flag: string, i: number) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              <Icon name="ExclamationTriangleIcon" size={14} className="text-accent" />
                              <span className="text-accent">{flag}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No red flags detected</p>
                      )}
                    </div>
                  </div>

                  {/* Transcript */}
                  {call.transcript && (
                    <div>
                      <h4 className="font-semibold text-foreground mb-2">Full Transcript</h4>
                      <div className="bg-background/50 rounded-lg p-4 border border-sage-200/20 max-h-48 overflow-y-auto">
                        <p className="text-sm text-foreground whitespace-pre-wrap">
                          {call.transcript}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Load More */}
      {calls.length >= limit && (
        <div className="p-4 border-t border-sage-200/30 text-center">
          <button
            onClick={() => setLimit(limit + 10)}
            className="px-6 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-smooth font-medium"
          >
            Load More
          </button>
        </div>
      )}
    </div>
  );
};

export default CallHistory;